import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiEye, FiSearch } from 'react-icons/fi';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' }) : '—');

// Read-only cross-shop customer directory. Click a customer to open their
// complete report (orders, bills, who billed, payments, timeline).
export default function SuperAdminCustomers() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => toast.error('Failed to load shops'));
  }, []);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.get('/customers', {
        params: { shopId: shopId || undefined, search: query || undefined, page, limit: 20 },
      });
      setCustomers(res.data.customers);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load customers'); }
    setLoading(false);
  }, [shopId, query, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Shop</label>
          <select className="form-control" style={{ width: 220 }} value={shopId}
            onChange={e => { setShopId(e.target.value); setPage(1); }}>
            <option value="">All Shops</option>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 12, color: '#94a3b8' }} />
            <input className="form-control" style={{ width: 260, paddingLeft: 30 }} placeholder="Search name or phone"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span style={{ color: '#64748b', fontSize: 13 }}>{total} customers</span>
        </div>
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Shop</th>
                  <th>Orders</th>
                  <th>Bills</th>
                  <th>Billed</th>
                  <th>Paid</th>
                  <th>Remaining</th>
                  <th>Last Bill</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 && (
                  <tr><td colSpan={9}>
                    <div className="empty-state"><div className="empty-state-icon">👤</div><h3>No customers found</h3></div>
                  </td></tr>
                )}
                {customers.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/superadmin/customers/${c._id}`)}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#3b82f6' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.phone}</div>
                    </td>
                    <td>{c.shop?.name || '—'}</td>
                    <td>{c.orders}</td>
                    <td>{c.bills}</td>
                    <td><strong>SAR {c.billed.toFixed(0)}</strong></td>
                    <td style={{ color: '#10b981', fontWeight: 600 }}>SAR {c.paid.toFixed(0)}</td>
                    <td style={{ color: c.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>SAR {c.remaining.toFixed(0)}</td>
                    <td>{fmtDate(c.lastBillAt)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm btn-icon" title="Open report"
                        onClick={(e) => { e.stopPropagation(); navigate(`/superadmin/customers/${c._id}`); }}>
                        <FiEye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div style={{ padding: '12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
